import React, { useEffect, useState } from "react";
import "leaflet/dist/leaflet.css";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { api, errMsg } from "../../lib/api";
import { Card, Select } from "../../components/UI";
import { toast } from "sonner";
import { Loader2, MapPin, QrCode, TrendingUp } from "lucide-react";

export const ScanMap = () => {
  const [data, setData] = useState(null);
  const [trends, setTrends] = useState(null);
  const [dmId, setDmId] = useState("");

  useEffect(() => {
    const params = dmId ? { dm_id: dmId } : {};
    api.get("/direct-mail/scan-map", { params }).then(r => setData(r.data))
      .catch(e => toast.error(errMsg(e, "Could not load scan map")));
    api.get("/direct-mail/scan-trends", { params }).then(r => setTrends(r.data)).catch(() => setTrends({ series: [], by_city: [] }));
  }, [dmId]);

  if (!data) return <Card><div className="py-8 text-center text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline mr-2 text-cyan-300" />Loading scan map…</div></Card>;

  const points = data.points || [];

  return (
    <Card data-testid="scan-heatmap-card">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div>
          <h3 className="font-display text-lg font-bold flex items-center gap-2"><MapPin className="w-4 h-4 text-cyan-300" /> Scan heatmap</h3>
          <p className="text-xs text-slate-500 mt-0.5">Where your mailer QR codes are being scanned — geolocated automatically (city-level) as voters scan.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex gap-2">
            <span className="text-xs px-2.5 py-1 rounded-full border border-white/15 text-slate-300" data-testid="scanmap-total">{data.total_scans} scans</span>
            <span className="text-xs px-2.5 py-1 rounded-full border border-cyan-400/30 bg-cyan-400/10 text-cyan-300" data-testid="scanmap-located">{data.located} located</span>
          </div>
          <Select value={dmId} onChange={e => setDmId(e.target.value)} data-testid="scanmap-filter">
            <option value="">All mailers</option>
            {(data.campaigns || []).map(c => <option key={c.id} value={c.id}>{c.name} ({c.scans})</option>)}
          </Select>
        </div>
      </div>
      <div className="rounded-lg overflow-hidden border border-white/10 relative" style={{ height: 380 }}>
        <MapContainer center={[39.5, -96.5]} zoom={4} style={{ height: "100%", width: "100%" }} data-testid="scanmap-leaflet">
          <TileLayer
            url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
            attribution='&copy; OpenStreetMap &copy; CARTO'
          />
          {points.map((p, i) => (
            <CircleMarker key={i} center={[p.lat, p.lon]}
              radius={Math.min(6 + Math.log2(p.count + 1) * 4, 24)}
              pathOptions={{ color: "var(--accent-primary)", fillColor: "var(--accent-primary)", fillOpacity: 0.45, weight: 1.5 }}>
              <Popup>
                <div style={{ fontSize: 12 }}>
                  <b>{p.city || "Unknown city"}{p.region ? `, ${p.region}` : ""}</b><br />
                  {p.count} scan{p.count === 1 ? "" : "s"}
                </div>
              </Popup>
            </CircleMarker>
          ))}
        </MapContainer>
        {points.length === 0 && (
          <div className="absolute inset-0 z-[1000] flex items-center justify-center bg-black/50 pointer-events-none" data-testid="scanmap-empty">
            <div className="text-center px-6">
              <QrCode className="w-8 h-8 mx-auto text-cyan-300/70 mb-2" />
              <div className="text-sm text-slate-200 font-medium">No located scans yet</div>
              <div className="text-xs text-slate-400 mt-1 max-w-xs">Scans are geolocated automatically from here on — try scanning a mailer QR with your phone to see your first dot appear.</div>
            </div>
          </div>
        )}
      </div>

      {trends && (
        <div className="mt-4 grid lg:grid-cols-3 gap-4" data-testid="scan-trends-section">
          <div className="lg:col-span-2 rounded-lg border border-white/10 bg-white/[0.02] p-4">
            <div className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-2 flex items-center gap-1.5">
              <TrendingUp className="w-3.5 h-3.5 text-cyan-300" /> Scans per day — last 30 days
            </div>
            <div className="h-44">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={trends.series || []}>
                  <CartesianGrid stroke="rgb(var(--contrast-rgb) / 0.05)" vertical={false} />
                  <XAxis dataKey="date" tick={{ fill: "var(--text-muted)", fontSize: 9 }} tickFormatter={v => (v || "").slice(5)} interval={4} />
                  <YAxis tick={{ fill: "var(--text-muted)", fontSize: 10 }} allowDecimals={false} width={28} />
                  <Tooltip contentStyle={{ background: "var(--bg-elevated)", border: "1px solid rgb(var(--contrast-rgb) / 0.1)", borderRadius: 8, fontSize: 12 }} cursor={{ fill: "rgb(var(--contrast-rgb) / 0.04)" }} />
                  <Bar dataKey="count" name="Scans" fill="var(--accent-primary)" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4" data-testid="scan-top-cities">
            <div className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-2">Top cities</div>
            {(trends.by_city || []).length === 0 ? (
              <div className="text-xs text-slate-600 py-4">No located scans yet — city rankings appear as scans come in.</div>
            ) : (
              <div className="space-y-1.5 max-h-44 overflow-y-auto">
                {trends.by_city.map((c, i) => (
                  <div key={`${c.city}-${c.region}`} className="flex items-center gap-2 text-xs" data-testid={`city-row-${i}`}>
                    <span className="text-slate-600 w-4">{i + 1}.</span>
                    <span className="text-slate-200 flex-1 truncate">{c.city || "Unknown"}{c.region ? `, ${c.region}` : ""}</span>
                    <span className="text-cyan-300 font-medium">{c.count}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  );
};
